/**
 * The GTM surface, typed.
 *
 * Every route here takes **no credential**. That is not an oversight to tighten later — it is
 * the whole point of the free tier, and `test_ui_contract.py` fails if any of them grows an
 * auth dependency, because the failure mode is a marketing page that goes blank for everyone
 * while the backend's own tests keep passing.
 *
 * The shapes are what `GTM_CONTRACT` pins. A field rendered here that is not in that list is a
 * field that can disappear without anything failing first.
 */

import { Client, apiBase } from "@authoxi/ui-shared";

export const client = new Client({ baseUrl: apiBase() });

// ── the grader ────────────────────────────────────────────────────────────────────────

export interface Check {
  check_id: string;
  title: string;
  passed: boolean;
  weight: number;
  points: number;
  detail: string;
  fix: string;
}

export interface Scan {
  scan_id: string;
  url: string;
  host: string;
  score: number;
  letter: string;
  checks: Check[];
  share_url: string;
  badge_url: string;
  created_at: string;
}

export interface Diff {
  host: string;
  scan_id: string;
  compared_to: string | null;
  score_delta: number | null;
  letter_before: string | null;
  letter_after: string;
  changes: { name: string; change: string; detail: string }[];
  summary: string;
}

export interface Snippet {
  share_url: string;
  badge_url: string;
  live_badge_url: string;
  markdown: string;
}

export const grade = (url: string) => client.post<Scan>("/grade", { url });
export const readScan = (id: string) => client.get<Scan>(`/grade/${encodeURIComponent(id)}`);
export const readDiff = (id: string) => client.get<Diff>(`/grade/${encodeURIComponent(id)}/diff`);
export const readSnippet = (id: string) =>
  client.get<Snippet>(`/grade/${encodeURIComponent(id)}/snippet`);
export const watchHost = (id: string, email: string) =>
  client.post<{ ok: boolean; host: string; unsubscribe_url: string; note: string }>(
    `/grade/${encodeURIComponent(id)}/watch`,
    { email },
  );

// ── the directory, leaderboard and report ─────────────────────────────────────────────

export interface DirectoryRow {
  scan_id: string;
  host: string;
  score: number;
  letter: string;
  checks_passed: number;
  checks_total: number;
  scanned_at: string;
  share_url: string;
  badge_url: string;
}

export interface DirectoryPage {
  hosts: DirectoryRow[];
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface LeaderboardRow {
  rank: number;
  host: string;
  letter: string;
  score: number;
  checks_passed: number;
  checks_total: number;
  checked: string;
  url: string;
}

export interface Leaderboard {
  sample_size: number;
  sample_note: string;
  measures: string;
  ranking: LeaderboardRow[];
}

export interface Report {
  sample_size: number;
  sample_note: string;
  grades: Record<string, number>;
  median_score: number;
  checks: { check_id: string; title: string; passed: number; seen: number }[];
  /** Below ten scanned endpoints the percentages are withheld — deliberately. The UI has to
   *  render BOTH states; assuming they are always present shows blanks on a fresh index and
   *  looks like a bug. */
  percentages_withheld: boolean;
}

export interface AgentPage {
  agent_id: string;
  name: string | null;
  did: string;
  kind: string;
  principal_did: string | null;
  acts_for_human: boolean;
  registered: string;
  verify: { key_directory: string; descriptor: string; signature_agent: string };
}

export const directory = (limit = 25, offset = 0) =>
  client.get<DirectoryPage>(`/directory.json?limit=${limit}&offset=${offset}`);
/** NOT `Scan`. The directory's detail route returns `scanned_at` where `POST /grade`
 *  returns `created_at` — same row, two names, and a hand-written type happily let the
 *  page render `undefined.slice()` into a blank screen. */
export interface HostDetail extends Omit<Scan, "created_at"> {
  scanned_at: string;
}

export const directoryHost = (host: string) =>
  client.get<HostDetail>(`/directory/${encodeURIComponent(host)}.json`);
export const directoryAgent = (id: string) =>
  client.get<AgentPage>(`/directory/agent/${encodeURIComponent(id)}.json`);
export const leaderboard = (limit = 25) =>
  client.get<Leaderboard>(`/leaderboard.json?limit=${limit}`);
export const report = () => client.get<Report>("/report/agent-traffic.json");

// ── whois ─────────────────────────────────────────────────────────────────────────────

export interface Whois {
  query: string;
  /** One of `did` · `agent_id` · `host` · `other-did` · `keyid` · `empty`. FIVE-plus states,
   *  not found/not-found — "that is a key thumbprint, which is not a global identifier" is a
   *  real answer and a more useful one than a shrug. */
  query_type: string;
  found: boolean;
  summary: string;
  facts: Record<string, string>;
  links: Record<string, string>;
  note: string;
}

export const whois = (q: string) => client.get<Whois>(`/whois.json?q=${encodeURIComponent(q)}`);

// ── sign in with your agent ───────────────────────────────────────────────────────────

export const signinNonce = () => client.get<{ nonce: string; expires_in: number }>(
  "/agent/signin/nonce",
);
